import {request} from "../api";

const SHOULD_SEND_ANALYTICS =
  process.env.NODE_ENV === "production" || process.env.PUBLIC_ENABLE_LOCAL_ANALYTICS === "true";

// Test-only override. The guard above is evaluated at module load, so a unit
// test running in a "test" NODE_ENV can't get past it without this.
let _forceSendForTesting = false;

// Events tracked before the short JWT is issued would go out without an
// Authorization header and be rejected with 401, silently dropping early
// telemetry (most importantly the initial page.viewed). We queue them here
// and flush from useAuth once setShortJwt fires.
const _pendingQueue = [];
let _authReady = false;

// Auto-enrichment context. SettingContextProvider syncs the current
// product/channel/contentType here on every change. track() merges these
// into properties as a base; caller-supplied keys override (e.g.
// scene.loaded uses the loaded scene's product, not the current selection).
let _currentContext = {};

function isRendererDocument() {
  const browserWindow = globalThis.window;
  if (!browserWindow) return false;
  if (browserWindow.__NEUROBAKER_RENDERER__ === true) return true;
  return new URLSearchParams(browserWindow.location?.search || "").get("render") === "true";
}

/**
 * Update the auto-enrichment context. Called from SettingContextProvider
 * whenever product/channel/contentType change. Undefined values are
 * dropped so the singleton never carries stale keys.
 */
export function setAnalyticsContext({product, channel, contentType} = {}) {
  const next = {};
  if (product !== undefined) next.product = product;
  if (channel !== undefined) next.channel = channel;
  if (contentType !== undefined) next.contentType = contentType;
  _currentContext = next;
}

function send(event, properties) {
  // The shared request path refreshes expired tokens, while cookies preserve same-origin authentication.
  request(`/analytics/event`, {
    method: "POST",
    credentials: "include",
    body: JSON.stringify({event, properties}),
    keepalive: true,
  }).catch(() => {});
}

/**
 * Fire-and-forget analytics event.
 * Uses the same JWT auth as the rest of the API. Never throws — analytics
 * must never break the app. Events queued before auth is ready are flushed
 * via flushPendingAnalytics() from useAuth.
 *
 * `properties` is merged on top of the auto-enrichment context (product,
 * channel, contentType from SettingContextProvider). Caller keys win.
 */
export function track(event, properties = {}) {
  if (!SHOULD_SEND_ANALYTICS && !_forceSendForTesting) return;
  if (isRendererDocument()) return;
  try {
    const enriched = {..._currentContext, ...(properties || {})};
    if (!_authReady) {
      _pendingQueue.push({event, properties: enriched});
      return;
    }
    send(event, enriched);
  } catch {
    // Swallow synchronous errors
  }
}

/**
 * Called by useAuth once auth has resolved in either direction. Flips the
 * ready flag and drains the pre-auth queue. Safe to call multiple times —
 * idempotent.
 */
export function flushPendingAnalytics() {
  _authReady = true;
  if (isRendererDocument()) {
    _pendingQueue.length = 0;
    return;
  }
  while (_pendingQueue.length) {
    const {event, properties} = _pendingQueue.shift();
    try {
      send(event, properties);
    } catch {
      /* swallow */
    }
  }
}

/**
 * Test-only helper. Resets the module singleton state so a unit test can
 * exercise the pre-auth queue without inheriting readiness from a prior
 * test. Not exported via index — call it explicitly from tests.
 */
export function resetAnalyticsForTesting() {
  _authReady = false;
  _pendingQueue.length = 0;
  _currentContext = {};
  _forceSendForTesting = false;
}

/** Test-only: enable sends even in NODE_ENV=test. */
export function forceSendForTesting(value) {
  _forceSendForTesting = !!value;
}
